import {atom, useAtom, useSetAtom} from 'jotai';
import {Fragment, useEffect, useRef} from 'react';
import {
  Location,
  useLocation,
  useNavigate,
  useNavigationType,
} from 'react-router-dom';
import styled from 'styled-components';
import {AccessRestrictionType} from '@/enums';
import {openChannelTalk} from '@/hooks/channelTalk';
import {useEscpacePress} from '@/hooks/helper';
import {LG_UPLUS_PATH, paths} from '@/router';
import {palette, Z_INDEX} from '@/styles';
import {Button} from '../Button';
import {Modal} from '../Modal';
import WarningIcon from './assets/warning.png'; // Figma 창업하기 7918:69710

const accessRestrictionModalAtom = atom<{
  type: AccessRestrictionType;
  fromMap: boolean; // 지도 화면에서 막힌 경우 닫으면 지도를 벗어난다
} | null>(null);

// 차단 사유·내부 용어(블랙리스트, 차단 등)는 노출하지 않는다. 본문은 프로토타입의 줄바꿈 단위로 나눈다.
const CONTENT = {
  [AccessRestrictionType.Account]: {
    title: '이용이 제한된 계정이에요',
    desc: [
      '운영 정책에 따라 이 계정으로는',
      '마이프차 지도를 이용할 수 없어요.',
      '자세한 내용은 1:1 문의로 남겨 주세요.',
    ],
  },
  [AccessRestrictionType.Ip]: {
    title: '이용이 제한된 접속 환경이에요',
    desc: [
      '운영 정책에 따라 현재 접속 환경에서는',
      '마이프차 지도를 이용할 수 없어요.',
      '자세한 내용은 1:1 문의로 남겨 주세요.',
    ],
  },
};

const isMapPath = (pathname: string) =>
  pathname === paths.map_root || pathname.startsWith(`${paths.map_root}/`);

// 홈·로그인은 다시 지도로 보내지므로 지도를 벗어날 때 돌아갈 화면에서 제외한다.
const isReturnablePath = (pathname: string) =>
  !isMapPath(pathname) && ![paths.home, paths.signin].includes(pathname);

export const useAccessRestrictionModal = () => {
  const setModal = useSetAtom(accessRestrictionModalAtom);

  return (type: AccessRestrictionType) => {
    // 라우터 밖(공통 에러 핸들러)에서도 호출되므로 현재 경로는 window 에서 읽는다.
    const {pathname} = window.location;

    // 유플러스 앱 지도는 채널톡을 숨기는 별도 화면이라 안내 대상에서 제외한다.
    if (pathname.startsWith(LG_UPLUS_PATH)) {
      return;
    }

    setModal({type, fromMap: isMapPath(pathname)});
  };
};

export const AccessRestrictionModalSystem = () => {
  const [modal, setModal] = useAtom(accessRestrictionModalAtom);
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const prevLocation = useRef<Location>();
  // 지도에 들어오기 직전 화면. 지도에서 막혔을 때 [닫기]로 돌아갈 곳이다.
  const mapEntry = useRef<{from?: Location; key: string; pushed: boolean}>();

  useEffect(() => {
    const prev = prevLocation.current;
    prevLocation.current = location;

    if (!isMapPath(location.pathname)) {
      // 뒤로 가기 등으로 지도를 벗어났으면 지도 안내는 닫는다.
      if (modal?.fromMap) {
        setModal(null);
      }
      return;
    }

    if (!prev || !isMapPath(prev.pathname)) {
      mapEntry.current = {
        from: prev && isReturnablePath(prev.pathname) ? prev : undefined,
        key: location.key,
        pushed: navigationType === 'PUSH',
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // 직전 화면으로 돌아간다. 주소 직접 입력 등으로 직전 화면이 없으면 서비스 소개로 보낸다.
  const leaveMap = () => {
    const entry = mapEntry.current;

    if (!entry?.from) {
      navigate(paths.intro, {replace: true});
    } else if (entry.pushed && entry.key === location.key) {
      navigate(-1);
    } else {
      navigate(
        {pathname: entry.from.pathname, search: entry.from.search},
        {replace: true}
      );
    }
  };

  // 결제·쿠폰 등 지도 밖에서 막힌 경우에는 그 화면에 머문다.
  const onClose = () => {
    setModal(null);

    if (modal?.fromMap) {
      leaveMap();
    }
  };

  const onInquiry = () => {
    onClose();
    openChannelTalk();
  };

  useEscpacePress(modal ? onClose : undefined);

  if (!modal) {
    return null;
  }

  const content = CONTENT[modal.type];

  return (
    <Modal width={328} zIndex={Z_INDEX.modal + 1}>
      <Body>
        <Icon src={WarningIcon} alt="" />
        <Title>{content.title}</Title>
        <Description>
          {content.desc.map((line, idx) => (
            <Fragment key={idx}>
              {!!idx && <br />}
              {line}
            </Fragment>
          ))}
        </Description>
        <ButtonArea>
          <CloseButton
            variant="outline"
            color="black"
            size="mediumLarge"
            onClick={onClose}
          >
            닫기
          </CloseButton>
          <InquiryButton
            variant="fill"
            color="primary"
            size="mediumLarge"
            autoFocus
            onClick={onInquiry}
          >
            1:1 문의하기
          </InquiryButton>
        </ButtonArea>
      </Body>
    </Modal>
  );
};

const Body = styled.div`
  padding: 28px 20px 20px;
  text-align: center;
`;

const Icon = styled.img`
  display: block;
  width: 72px;
  height: 72px;
  margin: 0 auto 12px;
`;

const Title = styled.h2`
  margin: 0 0 8px;
  color: ${palette.black};
  font-size: 18px;
  font-weight: 600;
  line-height: 26px;
`;

const Description = styled.p`
  margin: 0 0 24px;
  color: ${palette.gray800};
  font-size: 14px;
  line-height: 22px;
`;

const ButtonArea = styled.div`
  display: flex;
  gap: 8px;
`;

// 보조 [닫기] 40% : 주 [1:1 문의하기] 60%
const CloseButton = styled(Button)`
  flex: 4;
  min-width: 0;
`;

const InquiryButton = styled(Button)`
  flex: 6;
  min-width: 0;
`;
